import {useEffect, useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useStepFocus} from '@/hooks/use-step-focus'
import {ArrowLeft, ArrowRight, KeyRound, User} from 'lucide-react'
import {useNavigate, useLocation} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {ExpirationDateRow} from '@/components/shared/ExpirationDateRow'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {ErrorBanner} from '@/components/ui/error-banner'
import type {AccessProfileListItem} from '@/types/access-profile'
import {AccessRequestCreateRequestFields} from '@/types/access-request'
import type {AccessRequestBulkCreateRequest, AccessRequestItemCreate, BulkAccessRequest, PeerAccessProfile} from '@/types/access-request'
import {formatDateOnly, isValidExpirationDate} from '@/lib/date-utils'
import {MutedText} from '@/components/shared/MutedText'
import {StatusText} from '@/components/shared/StatusText'
import {FormField} from '@/components/shared/FormField'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import type {ComboboxOption} from '@/components/ui/combobox'
import {BeneficiaryMultiSelect} from '@/components/shared/BeneficiaryMultiSelect'
import {useAnnouncer} from '@/components/shared/Announcer'
import {AssociationEditor} from '@/components/shared/AssociationEditor'
import {HoldingStatusBadge} from '@/components/shared/HoldingStatusBadge'
import {useAccessProfileHoldings} from '@/hooks/useHoldingStatuses'
import {HoldingStatusKindBadge} from '@/types/holding-status'
import {PageLayout} from '@/components/shared/PageLayout'
import {PeerRecommendationList} from '@/components/shared/PeerRecommendationList'
import type {WizardStep as WizardStepDef} from '@/components/shared/WizardStepper'
import type {AssociationColumn} from '@/components/shared/AssociationEditor'
import {CONCEPT_ICON} from '@/lib/concepts'
import {ApiError} from '@/api/client'
import {BulkValidationErrorBanner, type BulkValidationErrorEntry} from '@/components/shared/BulkValidationErrorBanner'
import type {BulkValidationErrorParams} from '@/types/access-request'

const MAX_BENEFICIARIES = 50
const AMBER_THRESHOLD = 49
const PICKER_RESULT_LIMIT = 5

interface BeneficiaryChip {
    id: string
    displayName: string
}

const WizardStep = {
    SELECT_BENEFICIARY: 0,
    SELECT_PROFILES: 1,
    REVIEW: 2,
} as const

interface WizardItem {
    profile: AccessProfileListItem
    expiresAt: string
}

function extractPerBeneficiaryErrors(error: unknown): BulkValidationErrorEntry[] | null {
    if (!(error instanceof ApiError)) return null
    if (error.code !== 'AR0019') return null
    const params = error.params as unknown as BulkValidationErrorParams | undefined
    if (!params?.perBeneficiaryErrors) return []
    return params.perBeneficiaryErrors.map(entry => ({
        beneficiaryIdentityId: entry.beneficiaryIdentityId,
        errors: entry.errors.map(err => ({code: err.code, message: err.message})),
    }))
}

export function AccessRequestWizard() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.accessRequests.newPageTitle)
    const navigate = useNavigate()
    const location = useLocation()
    const {user} = useAuth()
    const {announce} = useAnnouncer()

    const STEP_LABELS = [i18n.accessRequests.beneficiaries, i18n.accessRequests.wizardSelectProfiles, i18n.accessRequests.wizardReviewSubmit]
    const wizardSteps: WizardStepDef[] = STEP_LABELS.map((label, i) => ({id: String(i), title: String(label)}))

    const isMyRoute = location.pathname.startsWith(`${routes.my.root()}/`)

    const backPath = isMyRoute ? routes.my.requests.list() : routes.accessGovernance.accessRequests.list()

    const [step, setStep] = useState(0)
    const [isSelfRequest, setIsSelfRequest] = useState(true)
    const selfChip: BeneficiaryChip | null = useMemo(() => (
        user?.identityId ? {id: user.identityId, displayName: user.name || user.identityId} : null
    ), [user?.identityId, user?.name])
    const [othersChips, setOthersChips] = useState<BeneficiaryChip[]>([])
    const [beneficiarySearch, setBeneficiarySearch] = useState('')

    const selectedBeneficiaries: BeneficiaryChip[] = isSelfRequest
        ? (selfChip ? [selfChip] : [])
        : othersChips
    const [profileSearch, setProfileSearch] = useState('')
    const [selectedItems, setSelectedItems] = useState<WizardItem[]>([])
    const [justification, setJustification] = useState('')
    const [bulkErrors, setBulkErrors] = useState<BulkValidationErrorEntry[] | null>(null)
    const [genericError, setGenericError] = useState<Error | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const beneficiarySearchQuery = useQuery({
        queryKey: ['beneficiary-search', beneficiarySearch],
        queryFn: () => services.identities.list({q: beneficiarySearch || undefined, size: PICKER_RESULT_LIMIT}),
        enabled: step === 0 && !isSelfRequest && (beneficiarySearch === '' || beneficiarySearch.length >= 2),
    })

    const peerBeneficiaryId = selectedBeneficiaries[0]?.id ?? ''
    const {data: profileResults} = useQuery({
        queryKey: ['profile-search', profileSearch],
        queryFn: () => services.accessProfiles.list({search: profileSearch, size: 50, status: 'ACTIVE'}),
        enabled: step === 1,
    })

    const {data: peerProfiles} = useQuery({
        queryKey: ['peer-recommendations', peerBeneficiaryId],
        queryFn: () => services.accessRequests.peerRecommendations(peerBeneficiaryId),
        enabled: step === 1 && !!peerBeneficiaryId && selectedBeneficiaries.length === 1,
    })

    const singleBeneficiaryId = selectedBeneficiaries.length === 1 ? selectedBeneficiaries[0].id : undefined
    const selectedProfileIds = useMemo(
        () => selectedItems.map((i) => i.profile.id),
        [selectedItems],
    )
    const {data: profileHoldings} = useAccessProfileHoldings(singleBeneficiaryId, selectedProfileIds)
    const hasBlockingHolding = useMemo(
        () => selectedProfileIds.some((id) => HoldingStatusKindBadge.of(profileHoldings?.[id]?.kind)?.blocking === true),
        [profileHoldings, selectedProfileIds],
    )

    const createMutation = useEntityMutation<BulkAccessRequest, Error, AccessRequestBulkCreateRequest>({
        mutationFn: (req) => services.accessRequests.createBulk(req),
        invalidate: ['my-requests'],
        onSuccess: (data) => {
            const count = data.createdRequests.length
            const message = count === 1
                ? i18n.accessRequests.bulkSuccess_one
                : i18n.accessRequests.bulkSuccess_other({count})
            // WHY: useEntityMutation only knows the static successMessage; we
            // dispatch ourselves so the count-aware singular/plural string can be picked.
            import('@/hooks/use-toast').then(({toast}) => toast({description: String(message)}))
            announce(String(message))
            /* WHY: a single-beneficiary submit drops the user on the request's
               detail page (preserves single-item UX from before bulk-create
               landed in 674). Bulk submits cover multiple beneficiaries, so
               there is no single detail to land on — go to the list. */
            const singleId = data.createdRequests.length === 1 ? data.createdRequests[0].id : null
            if (isMyRoute) {
                navigate(singleId ? routes.my.requests.detail(singleId) : routes.my.requests.list())
            } else {
                navigate(singleId ? routes.accessGovernance.accessRequests.detail(singleId) : routes.accessGovernance.accessRequests.list())
            }
        },
        onError: (error) => {
            setSubmitting(false)
            const perBenef = extractPerBeneficiaryErrors(error)
            if (perBenef !== null) {
                // WHY: snapshot displayName from current chips at error time so the
                // banner survives the user going back and swapping beneficiaries.
                const enriched = perBenef.map((entry) => ({
                    ...entry,
                    beneficiaryDisplayName: selectedBeneficiaries.find((c) => c.id === entry.beneficiaryIdentityId)?.displayName,
                }))
                setBulkErrors(enriched)
                setGenericError(null)
            } else {
                setBulkErrors(null)
                setGenericError(error)
            }
        },
    })

    useEffect(() => {
        if (createMutation.isPending) setSubmitting(true)
    }, [createMutation.isPending])

    const stepContainerRef = useStepFocus(step, String(STEP_LABELS[step]))

    const addBeneficiary = (id: string, displayName: string) => {
        if (othersChips.some((c) => c.id === id)) return
        if (othersChips.length >= MAX_BENEFICIARIES) return
        setOthersChips([...othersChips, {id, displayName}])
        setBeneficiarySearch('')
    }
    const removeBeneficiary = (id: string) => {
        setOthersChips(othersChips.filter((c) => c.id !== id))
    }
    const beneficiaryOptions: ComboboxOption[] = useMemo(() => {
        const data = beneficiarySearchQuery.data
        const content = (data && 'content' in data) ? data.content : []
        return content
            .filter((row) => !othersChips.some((c) => c.id === row.id))
            .map((row) => ({value: row.id, label: row.party.displayName ?? row.id}))
    }, [beneficiarySearchQuery.data, othersChips])

    const counterCount = othersChips.length
    const isAtCap = counterCount >= MAX_BENEFICIARIES
    const isAmber = counterCount >= AMBER_THRESHOLD && !isAtCap
    const counterTone: 'danger' | 'warning' | null = isAtCap ? 'danger' : isAmber ? 'warning' : null

    const addProfile = (profile: AccessProfileListItem) => {
        if (selectedItems.some(i => i.profile.id === profile.id)) return
        setSelectedItems([...selectedItems, {profile, expiresAt: ''}])
    }
    const removeProfile = (profileId: string) => {
        setSelectedItems(selectedItems.filter(i => i.profile.id !== profileId))
    }
    const updateItemExpiresAt = (profileId: string, value: string) => {
        setSelectedItems(selectedItems.map(i =>
            i.profile.id === profileId ? {...i, expiresAt: value} : i
        ))
    }

    const canProceed = () => {
        switch (step) {
            case WizardStep.SELECT_BENEFICIARY:
                return selectedBeneficiaries.length >= 1
            case WizardStep.SELECT_PROFILES:
                return selectedItems.length > 0 && selectedItems.every(i => {
                    if (i.profile.expiryRequired && !i.expiresAt) return false
                    if (!isValidExpirationDate(i.expiresAt, i.profile.maxDurationDays)) return false
                    return true
                })
            case WizardStep.REVIEW:
                return justification.trim().length > 0
            default:
                return false
        }
    }

    const handleSubmit = () => {
        setSubmitting(true)
        setBulkErrors(null)
        setGenericError(null)
        const items: AccessRequestItemCreate[] = selectedItems.map(i => ({
            accessProfileId: i.profile.id,
            // eslint-disable-next-line i18next/no-literal-string
            expiresAt: i.expiresAt ? `${i.expiresAt}T23:59:59Z` : undefined,
        }))
        const beneficiaryIdentityIds = selectedBeneficiaries.map((b) => b.id)
        createMutation.mutate({beneficiaryIdentityIds, justification, items})
    }

    const dismissBulkErrors = () => {
        setBulkErrors(null)
        setSubmitting(false)
    }

    const displayNameOf = (id: string): string => {
        const chip = selectedBeneficiaries.find((c) => c.id === id)
        return chip ? chip.displayName : id
    }

    const totalRequests = selectedBeneficiaries.length * selectedItems.length

    const handleBackspaceLastChip = () => {
        if (othersChips.length > 0) setOthersChips(othersChips.slice(0, -1))
    }

    const wizardNavigation = (
        <>
            {step === 0 ? (
                <Button variant="outline" onClick={() => navigate(backPath)} data-testid="btn-cancel">
                    {i18n.common.cancel}
                </Button>
            ) : (
                <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="btn-previous-step">
                    <Icon icon={ArrowLeft} size="sm" className="mr-2"/>
                    {i18n.common.back}
                </Button>
            )}
            {step < STEP_LABELS.length - 1 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} data-testid="btn-next-step">
                    {i18n.autoAssignRules.next}
                    <Icon icon={ArrowRight} size="sm" className="ml-2"/>
                </Button>
            ) : (
                <Button onClick={handleSubmit}
                        disabled={!canProceed() || submitting || bulkErrors !== null || hasBlockingHolding}
                        data-testid="btn-submit-request">
                    {submitting ? i18n.accessRequests.submitting : i18n.accessRequests.submitRequest}
                </Button>
            )}
        </>
    )

    return (
        <PageLayout
            variant="wizard"
            data-testid="access-request-wizard-page"
            icon={CONCEPT_ICON.accessRequest}
            title={i18n.accessRequests.wizardTitle}
            backTo={backPath}
            wizardSteps={wizardSteps}
            wizardCurrentStep={step}
            wizardNavigation={wizardNavigation}
        >

            <div ref={stepContainerRef} data-testid="access-request-step-container">

            {/* Step 0: Beneficiary */}
            {step === 0 && (
                <Card data-help-prefix="accessRequestWizard">
                    <CardHeader>
                        <CardTitle data-testid="card-title-who-needs-access" data-help-key="_section">{i18n.accessRequests.whoNeedsAccess}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <InlineRow gap="md">
                            <Button
                                variant={isSelfRequest ? 'default' : 'outline'}
                                onClick={() => {
                                    setIsSelfRequest(true)
                                    setOthersChips([])
                                    setBeneficiarySearch('')
                                }}
                                data-testid="btn-select-myself">
                                <Icon icon={User} size="sm" className="mr-2"/>
                                {i18n.accessRequests.myself}
                            </Button>
                            <Button
                                variant={!isSelfRequest ? 'default' : 'outline'}
                                onClick={() => {
                                    setIsSelfRequest(false)
                                }}
                                data-testid="btn-select-someone-else">
                                <Icon icon={User} size="sm" className="mr-2"/>
                                {i18n.accessRequests.someoneElse}
                            </Button>
                        </InlineRow>

                        {!isSelfRequest && (
                            <Stack gap="sm">
                                <InlineRow justify="between" align="center">
                                    <Label htmlFor="beneficiary-multi-picker" data-help-key="picker">{i18n.accessRequests.searchForIdentity}</Label>
                                    {othersChips.length > 0 && (
                                        counterTone ? (
                                            <StatusText
                                                tone={counterTone}
                                                size="sm"
                                                data-testid="beneficiary-counter"
                                            >
                                                {String(i18n.accessRequests.beneficiariesCounter({count: counterCount}))}
                                            </StatusText>
                                        ) : (
                                            <MutedText
                                                size="sm"
                                                data-testid="beneficiary-counter"
                                            >
                                                {String(i18n.accessRequests.beneficiariesCounter({count: counterCount}))}
                                            </MutedText>
                                        )
                                    )}
                                </InlineRow>
                                <BeneficiaryMultiSelect
                                    chips={othersChips}
                                    options={beneficiaryOptions}
                                    onPick={(id) => {
                                        const data = beneficiarySearchQuery.data
                                        const content = (data && 'content' in data) ? data.content : []
                                        const row = content.find((r) => r.id === id)
                                        if (row) addBeneficiary(row.id, row.party.displayName ?? row.id)
                                    }}
                                    onRemove={removeBeneficiary}
                                    onSearch={setBeneficiarySearch}
                                    onSearchInputBackspace={handleBackspaceLastChip}
                                    isLoading={beneficiarySearchQuery.isLoading}
                                    disabled={isAtCap}
                                    placeholder={String(i18n.accessRequests.searchBeneficiariesPlaceholder)}
                                    emptyMessage={beneficiarySearch.length === 1
                                        ? String(i18n.accessRequests.pickerHintTypeMore)
                                        : String(i18n.accessRequests.pickerNoResults)}
                                    data-testid="beneficiary-multi-picker"
                                />
                                {isAtCap && (
                                    <StatusText
                                        tone="danger"
                                        size="sm"
                                        data-testid="beneficiary-cap-message"
                                    >
                                        {i18n.accessRequests.beneficiariesMax}
                                    </StatusText>
                                )}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Step 1: Select Access Profiles (with inline expiration) */}
            {step === 1 && (
                <Stack>
                    {peerProfiles && peerProfiles.length > 0 && (
                        <PeerRecommendationList
                            peers={peerProfiles}
                            isAlreadySelected={(peer: PeerAccessProfile) =>
                                selectedItems.some(i => i.profile.id === peer.accessProfile.id)}
                            onAdd={(peer: PeerAccessProfile) => {
                                const fullProfile = profileResults?.content.find(p => p.id === peer.accessProfile.id)
                                addProfile(fullProfile ?? ({
                                    id: peer.accessProfile.id,
                                    name: peer.accessProfile.displayName,
                                    resource: peer.resource,
                                    environment: peer.environment,
                                    expiryRequired: peer.expiryRequired,
                                    maxDurationDays: peer.maxDurationDays,
                                } as unknown as AccessProfileListItem))
                            }}
                        />
                    )}

                    {/* Access Profile Search + Selected Items */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm" data-testid="card-title-select-access-profiles">{i18n.accessRequests.selectAccessProfiles}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {(() => {
                                const columns: AssociationColumn<WizardItem>[] = [
                                    {
                                        header: String(i18n.common.name),
                                        render: (item) => (
                                            <InlineRow gap="sm" className="min-w-0">
                                                <Icon icon={KeyRound} muted className="shrink-0"/>
                                                <Strong truncate>{item.profile.name}</Strong>
                                            </InlineRow>
                                        ),
                                    },
                                    {
                                        header: String(i18n.accessProfiles.resource),
                                        render: (item) => <MutedText size="xs">{item.profile.resource.displayName}</MutedText>,
                                    },
                                    {
                                        header: '',
                                        render: (item) => (
                                            <HoldingStatusBadge
                                                holding={profileHoldings?.[item.profile.id]}
                                                data-testid={`holding-status-${item.profile.id}`}
                                            />
                                        ),
                                        // eslint-disable-next-line i18next/no-literal-string -- CSS className, not user text
                                        className: 'w-0',
                                    },
                                    {
                                        header: '',
                                        render: (item) => item.profile.expiryRequired
                                            ? <Badge variant="outline" className="text-xs whitespace-nowrap">{i18n.accessRequests.expiryRequired}</Badge>
                                            : null,
                                        // eslint-disable-next-line i18next/no-literal-string -- CSS className, not user text
                                        className: 'w-0',
                                    },
                                ]

                                const profileToWizardItem = (profile: AccessProfileListItem): WizardItem => ({
                                    profile,
                                    expiresAt: '',
                                                        })

                                return (
                                    <AssociationEditor<WizardItem>
                                        items={selectedItems}
                                        onSearch={setProfileSearch}
                                        searchResults={(profileResults?.content ?? []).map(profileToWizardItem)}
                                        onAdd={(item) => addProfile(item.profile)}
                                        onRemove={(item) => removeProfile(item.profile.id)}
                                        columns={columns}
                                        getKey={(item) => item.profile.id}
                                        getLabel={(item) => item.profile.name}
                                        searchPlaceholder={String(i18n.accessRequests.searchAccessProfiles)}
                                        emptyMessage={String(i18n.accessRequests.noProfilesSelected)}
                                        testId="access-profile-association-editor"
                                        renderItemExtra={(item) => (
                                            <ExpirationDateRow
                                                value={item.expiresAt}
                                                onChange={v => updateItemExpiresAt(item.profile.id, v)}
                                                required={item.profile.expiryRequired}
                                                maxDurationDays={item.profile.maxDurationDays}
                                                label={String(i18n.accessRequests.expirationDate)}
                                                maxDurationHint={item.profile.maxDurationDays ? String(i18n.accessRequests.maxDurationHint({days: item.profile.maxDurationDays})) : undefined}
                                                testId={`expiration-date-${item.profile.id}`}
                                            />
                                        )}
                                    />
                                )
                            })()}
                        </CardContent>
                    </Card>
                </Stack>
            )}

            {/* Step 2: Review & Submit */}
            {step === 2 && (
                <Stack>
                    {bulkErrors && bulkErrors.length > 0 && (
                        <BulkValidationErrorBanner
                            entries={bulkErrors}
                            displayNameOf={displayNameOf}
                            onDismiss={dismissBulkErrors}
                        />
                    )}
                    <Card>
                        <CardHeader>
                            <CardTitle data-testid="card-title-review-and-submit">{i18n.accessRequests.reviewAndSubmit}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <MutedText size="sm" className="block">{i18n.accessRequests.beneficiaries}</MutedText>
                                <InlineRow wrap gap="sm" data-testid="review-beneficiary-chips">
                                    {selectedBeneficiaries.map((b) => (
                                        <Badge key={b.id} variant="outline" data-testid={`review-beneficiary-${b.id}`}>
                                            {b.displayName}
                                        </Badge>
                                    ))}
                                </InlineRow>
                            </div>
                            <div>
                                <MutedText size="sm" className="mb-2 block">{i18n.accessRequests.reviewAccessProfiles({count: selectedItems.length})}</MutedText>
                                {selectedItems.map(item => (
                                    <InlineRow key={item.profile.id} gap="sm" className="py-1 text-sm" data-testid="review-profile-item">
                                        <Icon icon={KeyRound} size="xs" muted/>
                                        {item.profile.name}
                                        {item.expiresAt && (
                                            <MutedText>
                                                {i18n.accessRequests.untilDate({date: formatDateOnly(item.expiresAt)})}
                                            </MutedText>
                                        )}
                                    </InlineRow>
                                ))}
                            </div>
                            <div data-testid="review-expansion-summary">
                                <Strong>{
                                    String(i18n.accessRequests.reviewExpansion({
                                        beneficiaries: selectedBeneficiaries.length,
                                        items: selectedItems.length,
                                        total: totalRequests,
                                    }))
                                }</Strong>
                            </div>
                            <FormField
                                label={String(i18n.accessRequests.justification)}
                                htmlFor={AccessRequestCreateRequestFields.justification}
                                required
                            >
                                <Textarea id={AccessRequestCreateRequestFields.justification} value={justification}
                                          onChange={e => setJustification(e.target.value)}
                                          placeholder={i18n.accessRequests.justificationPlaceholder}
                                          rows={4}/>
                            </FormField>
                        </CardContent>
                    </Card>

                    {genericError && <ErrorBanner error={genericError}/>}
                </Stack>
            )}

            </div>
        </PageLayout>
    )
}
